import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Modal } from '@/components/Modal';
import { TextField } from '@/components/TextField';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { usePasswordComplexityPolicy } from '../hooks/usePasswordComplexityPolicy';
import { PasswordRequirements } from './PasswordRequirements';
import { UserAvatar } from '@/components/NavigationBar';

interface ResetPasswordFormValues {
    password: string;
    confirmPassword: string;
}

const DEFAULT_VALUES: ResetPasswordFormValues = {
    password: '',
    confirmPassword: '',
};

interface ResetPasswordModalProps {
    userId: string;
    userName: string;
    onClose: () => void;
}

export function ResetPasswordModal({
    userId,
    userName,
    onClose,
}: ResetPasswordModalProps) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const { policy } = usePasswordComplexityPolicy();

    const {
        register,
        watch,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<ResetPasswordFormValues>({
        mode: 'onChange',
        defaultValues: DEFAULT_VALUES,
    });

    const password = watch('password');

    const { isSubmitting, run } = useAsyncAction(
        async (values: ResetPasswordFormValues) => {
            await AdminSettingsService.setMemberPassword(
                userId,
                values.password,
            );
            onClose();
        },
        {
            successMessage: t('members.setPasswordSuccess'),
            errorMessage: t('members.resetPasswordFailed'),
        },
    );

    const onSubmit = handleSubmit((values) => run(values));

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={
                <div className="flex flex-col gap-2">
                    <span>
                        {t('members.resetPasswordTitle', { name: userName })}
                    </span>
                    <div className="flex items-center gap-2.5 text-xs text-grey-200 font-normal">
                        <UserAvatar name={userName} size="xs" />
                        <span>{userName}</span>
                    </div>
                </div>
            }
            onCancel={onClose}
            onSubmit={onSubmit}
            submitText={t('members.setNewPassword')}
            submitDisabled={isSubmitting || !isValid}
            submitInProgress={isSubmitting}
            size="small"
        >
            <div className="flex flex-col gap-4 px-6 py-6">
                <TextField
                    label={t('members.password')}
                    labelClassName="text-sm-medium text-white-100"
                    type="password"
                    required
                    {...register('password', {
                        validate: (value) => value.length > 0,
                    })}
                />
                <TextField
                    label={t('members.confirmPassword')}
                    labelClassName="text-sm-medium text-white-100"
                    type="password"
                    required
                    error={
                        errors.confirmPassword
                            ? t('members.passwordsMismatch')
                            : undefined
                    }
                    {...register('confirmPassword', {
                        validate: (value) => value === password,
                    })}
                />
                {policy && (
                    <PasswordRequirements password={password} policy={policy} />
                )}
            </div>
        </Modal>
    );
}
